import useMap from "./useMap";
import { memo, useCallback, useEffect, useMemo } from "react";
import { FIELD_GEOMETRIES_SOURCE } from "./layers";
import { Feature, FeatureCollection, MultiPolygon } from "geojson";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
} from "../utils/map";

import { Field } from "@/types/field";
import * as turf from "@turf/turf";
import {
    FillLayerSpecification,
    LineLayerSpecification,
    LngLatBoundsLike,
    MapMouseEvent,
    SymbolLayerSpecification,
} from "mapbox-gl";
import { useMatch, useNavigate } from "@tanstack/react-router";
import ImageryTimelineV2 from "../../components/field/ImageryTimelineV2";
import useRequest from "../request/useRequest";
import LayersMenu from "@/components/field/layers/LayersMenu";
import { useQuery } from "@tanstack/react-query";

const getMarkerColor = (feature: Feature<MultiPolygon, Field>) => {
    let color = "gray";
    if (feature.properties.recommendation.insights_active) {
        if (
            feature.properties.recommendation.user_recommendation != null &&
            feature.properties.recommendation.user_recommendation
                .recommend_application != null
        ) {
            color = feature.properties.recommendation.user_recommendation
                .recommend_application
                ? "red"
                : "green";
        } else if (feature.properties.recommendation.recent_application) {
            color = "green";
        } else if (feature.properties.recommendation.gap) {
            color = "gray";
        } else if (feature.properties.recommendation.recommend_fertigation) {
            color = "red";
        } else {
            color = "green";
        }
    } else {
        color = "white";
    }
    return color;
};

const ICON_LAYER: SymbolLayerSpecification = {
    source: FIELD_GEOMETRIES_SOURCE,
    id: "field-geoemtries-icon",
    type: "symbol",
    maxzoom: 14,
    layout: {
        "symbol-placement": "point",
        "icon-allow-overlap": true,
        "icon-image": [
            "case",
            ["==", "red", ["get", "marker_color"]],
            "location-red",
            ["==", "green", ["get", "marker_color"]],
            "location-green",
            ["==", "gray", ["get", "marker_color"]],
            "location-gray",
            ["==", "yellow", ["get", "marker_color"]],
            "location-yellow",
            "location-white",
        ],
        "icon-anchor": "bottom",
        "icon-size": 0.08,
    },
    paint: {
        "icon-halo-width": 15,
    },
};

const FILL_LAYER: FillLayerSpecification = {
    source: FIELD_GEOMETRIES_SOURCE,
    id: "field-geoemtries-boundary-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": "#808080",
        "fill-opacity": 0.3,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: FIELD_GEOMETRIES_SOURCE,
    id: "field-geoemtries-boundary-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": "#707070",
        "line-width": 3,
    },
    minzoom: 14,
};

function BaseMap() {
    const { axiosRequest } = useRequest();
    const navigate = useNavigate();
    const { mapContainerRef, map, mapLoaded, initializeMap } = useMap();
    const match = useMatch({
        from: "/dashboard/fields/$fieldId",
        shouldThrow: false,
    });

    const matchFields = useMatch({
        from: "/dashboard/fields/",
        shouldThrow: false,
    });

    const { data } = useQuery<FeatureCollection<MultiPolygon, Field>>({
        queryKey: ["fields/geometries/"],
        queryFn: () =>
            axiosRequest<FeatureCollection<MultiPolygon, Field>>({
                url: "fields/geometries/",
            }),
        enabled: mapLoaded && !!matchFields,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: Infinity,
    });

    useEffect(() => {
        if (mapContainerRef) {
            initializeMap();
        }
    }, [initializeMap, mapContainerRef]);

    const markerData = useMemo(() => {
        const markered =
            data?.features
                ?.filter((f) => !!f.geometry)
                .map((f) => {
                    return {
                        ...f,
                        properties: {
                            ...f.properties,
                            marker_color: getMarkerColor(f),
                        },
                    };
                }) ?? [];
        return turf.featureCollection(markered);
    }, [data?.features]);

    useEffect(() => {
        if (data && markerData && map && mapLoaded) {
            if (!map.getSource(FIELD_GEOMETRIES_SOURCE)) {
                addUpdateGeojsonSource(map, FIELD_GEOMETRIES_SOURCE, {
                    type: "geojson",
                    data: markerData,
                    tolerance: 0,
                });
            }

            if (!match) {
                addUpdateGeojsonLayers(map, [
                    FILL_LAYER,
                    ICON_LAYER,
                    LINE_LAYER,
                ]);
                if (markerData?.features?.length) {
                    map.fitBounds(
                        turf.bbox(
                            turf.featureCollection(
                                markerData?.features?.filter(
                                    (feature) =>
                                        (feature.properties
                                            .grower as unknown as number) !== 1,
                                ),
                            ),
                        ) as LngLatBoundsLike,
                        {
                            padding: 20,
                            duration: 0,
                        },
                    );
                }
            } else {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    ICON_LAYER.id,
                    LINE_LAYER.id,
                ]);
            }

            map.on("mouseenter", [FILL_LAYER.id, ICON_LAYER.id], () => {
                if (!map) return;
                map.getCanvas().style.cursor = "pointer";
            });

            map.on("mouseleave", [FILL_LAYER.id, ICON_LAYER.id], () => {
                if (!map) return;
                map.getCanvas().style.cursor = "";
            });
        }
    }, [data, map, mapLoaded, markerData, match]);

    const adddingField = useMatch({
        from: "/dashboard/fields/add",
        shouldThrow: false,
    });

    const addingBulkField = useMatch({
        from: "/dashboard/fields/add-bulk",
        shouldThrow: false,
    });

    const clickHandler = useCallback(
        (e: MapMouseEvent) => {
            if (!map) return;
            const features = map.queryRenderedFeatures(e.point, {
                layers: [FILL_LAYER.id, ICON_LAYER.id],
            });
            if (features?.length) {
                navigate({
                    to: "/dashboard/fields/$fieldId/insights/ntime",
                    params: { fieldId: features[0].id as string },
                    search: {
                        advisor: undefined,
                    },
                });
            }
        },
        [map, navigate],
    );

    useEffect(() => {
        if (map && mapLoaded) {
            if (!match && !adddingField && !addingBulkField) {
                map.on("click", clickHandler);
            } else {
                map.off("click", clickHandler);
            }
        }
    }, [
        adddingField,
        addingBulkField,
        clickHandler,
        map,
        mapLoaded,
        match,
        navigate,
    ]);

    return (
        <div className="flex flex-col w-full h-full">
            <div className="relative flex-1 min-h-0">
                <div
                    id="map"
                    ref={mapContainerRef}
                    className="bg-gray-200 w-full h-full"
                />
                {match && <LayersMenu />}
            </div>
            {match && (
                <div className="shrink-0 w-full bg-white border-t border-l border-default-200 flex flex-row gap-3 items-stretch">
                    <ImageryTimelineV2 />
                </div>
            )}
        </div>
    );
}
export default memo(BaseMap);
